import React, { useState } from "react";
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CompanyExpandRow from "./CompanyExpandRow";

/**
 * 企業種別の表示ラベル
 */
const COMPANY_ROLE_LABEL = {
  recruit: "リクルート企業",
  management: "マネジメント企業",
};

/**
 * 種別ラベルを表示用文字列に変換する
 */
const getCompanyRoleLabel = (role) => COMPANY_ROLE_LABEL[role] || "-";

// テーブルの列定義
const columns = [
  { label: "No", width: "3%" },
  { label: "企業名", width: "16%" },
  { label: "住所", width: "20%" },
  { label: "代表者", width: "12%" },
  { label: "メール", width: "20%" },
  { label: "電話番号", width: "12%" },
  { label: "種別", width: "14%" },
  { label: "", width: "3%" },
];

/**
 * 省略表示 + Tooltip 用共通スタイル
 */
const ellipsisCellSx = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/**
 * 省略表示 + Tooltip 用共通セル
 */
const EllipsisTooltipCell = ({ value }) => {
  const displayValue = value || "-";

  return (
    <TableCell align="center" sx={ellipsisCellSx}>
      <Tooltip title={displayValue} arrow placement="top">
        <Box component="span" sx={{ display: "block", ...ellipsisCellSx }}>
          {displayValue}
        </Box>
      </Tooltip>
    </TableCell>
  );
};

/**
 * 企業一覧テーブル
 *
 * 責務：
 * - 一覧を表示する
 * - 行の開閉を管理する
 *
 * 役割やメールの編集状態は CompanyExpandRow 側に任せる
 */
export default function CompanyTable({
  companies = [],
  pagination,
  currentPage,
  onSave,
  onDelete,
  fieldErrorsByCompany,
  clearCompanyFieldErrors,
}) {
  // 現在開いている会社ID
  const [openRowId, setOpenRowId] = useState(null);

  /**
   * 行の開閉を切り替える
   */
  const handleToggleRow = (companyId) => {
    setOpenRowId((prev) => (prev === companyId ? null : companyId));
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        maxWidth: "100%",
        borderRadius: 2,
        overflowX: "hidden",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
      }}
    >
      <Table sx={{ tableLayout: "fixed", width: "100%" }}>
        {/* テーブルヘッダー */}
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.label}
                align="center"
                sx={{
                  width: column.width,
                  fontWeight: "bold",
                  backgroundColor: "#EAF3FF",
                  borderBottom: "1px solid #d8e7fb",
                  py: 1.5,
                  whiteSpace: "nowrap",
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {companies.map((company, index) => {
            const isOpen = openRowId === company.id;

            return (
              <React.Fragment key={company.id}>
                {/* 一覧行 */}
                <TableRow
                  hover
                  onClick={() => handleToggleRow(company.id)}
                  sx={{
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#F9FBFF",
                    },
                  }}
                >
                  {/* No */}
                  <TableCell align="center">
                    {((pagination?.current_page ?? currentPage ?? 1) - 1) *
                      (pagination?.per_page || 10) +
                      index +
                      1}
                  </TableCell>

                  {/* 企業名 */}
                  <EllipsisTooltipCell value={company.name} />

                  {/* 住所 */}
                  <EllipsisTooltipCell value={company.address} />

                  {/* 代表者 */}
                  <EllipsisTooltipCell value={company.representative_name} />

                  {/* メール */}
                  <EllipsisTooltipCell value={company.email} />

                  {/* 電話番号 */}
                  <EllipsisTooltipCell value={company.phone_number} />

                  {/* 種別 */}
                  <TableCell align="center">
                    <Chip
                      label={getCompanyRoleLabel(company.role)}
                      size="small"
                      sx={{
                        fontWeight: 500,
                        backgroundColor: "#f2f7ff",
                        maxWidth: "100%",
                      }}
                    />
                  </TableCell>

                  {/* 開閉アイコン */}
                  <TableCell align="center" sx={{ width: 60 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isOpen ? (
                        <KeyboardArrowUpIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowDownIcon fontSize="small" />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>

                {/* 展開行 */}
                <CompanyExpandRow
                  company={company}
                  isOpen={isOpen}
                  onSave={onSave}
                  onDelete={onDelete}
                  fieldErrorsByCompany={fieldErrorsByCompany}
                  clearCompanyFieldErrors={clearCompanyFieldErrors}
                />
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
